import { Button, Spinner } from "@/components/ui";
import { useT } from "@/i18n";
import { canvasMessages } from "@/i18n/messages/canvas";
import { framesSharedMessages } from "@/i18n/messages/frames-shared";
import { cn } from "@/lib/cn";
import type { FrameData } from "@/lib/frame-data";
import { useFrameDetail } from "./use-frame-detail";
import { useFrameSurface } from "./use-frame-surface";

/**
 * What stands in for a live frame body while the frame is card-only: the
 * frame's empty surface with a quiet spinner while the detail loads (the
 * card's poster does not look like the frame), and a retry after a failed
 * load. The body mounts once the frame is `"full"`.
 */
export function FrameDetailLoading({ frame }: { frame: FrameData }) {
  const t = useT(framesSharedMessages);
  const tCanvas = useT(canvasMessages);
  const frameSurface = useFrameSurface(frame.frameId);
  const { failed, retry } = useFrameDetail(
    frame.frameId,
    frame.contentState === "card",
  );
  return (
    <div
      className={cn(
        frameSurface.className,
        "flex h-full w-full flex-col items-center justify-center gap-appkit-md overflow-hidden p-3 text-center",
      )}
      aria-busy={!failed}
      data-testid={`frame-detail-loading-${frame.frameId}`}
      {...frameSurface.attributes}
    >
      {failed ? (
        <>
          <p
            role="alert"
            className="text-appkit-body text-secondary"
            data-testid={`frame-detail-error-${frame.frameId}`}
          >
            {t("detailDidNotLoad")}
          </p>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={retry}
            data-testid={`frame-detail-retry-${frame.frameId}`}
          >
            {tCanvas("retry")}
          </Button>
        </>
      ) : (
        <Spinner
          className="h-4 w-4 text-muted"
          data-testid={`frame-detail-spinner-${frame.frameId}`}
        />
      )}
    </div>
  );
}
